import React, { useState, useEffect, useRef } from 'react';
import { TextField, Button, Box, Checkbox, FormControlLabel, Grid, FormControl, useTheme, useMediaQuery } from '@mui/material';
import debounce from 'lodash/debounce';
import ProductSelect from './ProductSelect';

const DetailPanelForm = ({ row, onSubmit, onChangeCode, columns, editableFields, primaryField }) => {
  const [formData, setFormData] = useState({});
  const [inputValues, setInputValues] = useState({});
  const [regularFields, setRegularFields] = useState([]);
  const [multilineFields, setMultilineFields] = useState([]);
  const [gridWidth, setGridWidth] = useState(0);
  const gridRef = useRef(null);
  const theme = useTheme();
  const [layout, setLayout] = useState([]);
  const fieldRefs = useRef({});

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const ignoredFields = ['SYNC_TIME'];
  const [booleanFields, setBooleanFields] = useState([]);

  useEffect(() => {
    setFormData(row);
    setInputValues(Object.fromEntries(Object.entries(row).map(([key, value]) => [key, value?.toString() || ''])));
  }, [row]);

  useEffect(() => {
    const filteredColumns = columns.filter(column => 
      (editableFields.includes(column.field) || column.field === primaryField) && !ignoredFields.includes(column.field)
    );

    setRegularFields(filteredColumns.filter(column => column.type !== 'boolean' && column.type !== 'multilineText'));
    setMultilineFields(filteredColumns.filter(column => column.type === 'multilineText'));
    setBooleanFields(filteredColumns.filter(column => column.type === 'boolean'));
  }, [columns, editableFields, primaryField]);

  const measureFieldWidth = (field) => {
    if (fieldRefs.current[field]) {
      return fieldRefs.current[field].offsetWidth;
    }
    return 0;
  };

  const calculateLayout = () => {
    if (!gridRef.current) return;

    const containerWidth = gridRef.current.offsetWidth;
    const fields = regularFields.map(column => column.field);
    const fieldWidths = fields.map(measureFieldWidth);

    let currentRow = [];
    let currentRowWidth = 0;
    const newLayout = [];

    fields.forEach((field, index) => {
      const fieldWidth = fieldWidths[index];
      if (currentRowWidth + fieldWidth <= containerWidth) {
        currentRow.push(field);
        currentRowWidth += fieldWidth;
      } else {
        newLayout.push(currentRow);
        currentRow = [field];
        currentRowWidth = fieldWidth;
      }
    });

    if (currentRow.length > 0) {
      newLayout.push(currentRow);
    }

    setLayout(newLayout);
  };

  useEffect(() => {
    const debouncedCalculateLayout = debounce(calculateLayout, 200);
    debouncedCalculateLayout();

    const resizeObserver = new ResizeObserver(debouncedCalculateLayout);
    if (gridRef.current) {
      resizeObserver.observe(gridRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [regularFields, formData]);

  const handleChange = (event, newValue, reason) => {
    const { name, value, type, checked } = event.target;
    if (type === 'checkbox') {
      setFormData(prevData => ({ ...prevData, [name]: checked }));
    } else {
      setFormData(prevData => ({ ...prevData, [name]: value }));
      setInputValues(prevValues => ({ ...prevValues, [name]: value }));
    }
  };

  const handleAutocompleteChange = (name, newValue) => {
    setFormData(prevData => ({
      ...prevData,
      [name]: newValue
    }));
    setInputValues(prevValues => ({
      ...prevValues,
      [name]: newValue || ''
    }));
  };


  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(formData);
  };

  const getFieldWidth = (value) => {
    const minWidth = 150; // Minimum width of 150px
    const charWidth = 10; // Approximate width of a character
    const calculatedWidth = Math.max(minWidth, (value?.toString().length || 0) * charWidth);
    return Math.min(calculatedWidth, 400); // Maximum width of 400px
  };

  const renderField = (column, ref) => {
    const fieldWidth = getFieldWidth(formData[column.field]);
    if (column.type === 'multilineText') {
      return (
        <TextField
          label={column.headerName}
          name={column.field}
          value={formData[column.field] || ''}
          onChange={handleChange}
          multiline
          rows={4}
          disabled={column.field === primaryField}
          fullWidth
        />
      );
    } else if (['singleSelect', 'multipleSelects'].includes(column.type)) {
      return (
        <FormControl sx={{ width: `${fieldWidth}px` }} ref={ref}>
          <ProductSelect
            id={`${column.field}-combo-box`}
            label={column.headerName}
            options={column.options || []}
            multiple={column.type === 'multipleSelects'}
            value={formData[column.field]}
            onChange={value => handleAutocompleteChange(column.field, value)}
            disabled={column.field === primaryField}
          />
        </FormControl>
      );
    } else {
      return (
        <TextField
          label={column.headerName}
          name={column.field}
          value={inputValues[column.field] || ''}
          onChange={handleChange}
          type={column.type === 'number' ? 'number' : 'text'}
          disabled={column.field === primaryField}
          sx={{ minWidth: '150px', width: `${fieldWidth}px` }}
          ref={ref}
        />
      );
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      ref={gridRef}
      sx={{
        p: 3,
        width: '100%',
        maxWidth: '1000px',
        marginLeft: 0
      }}
    >
      <Grid container spacing={2}>
        {layout.map((row, rowIndex) => (
          <React.Fragment key={rowIndex}>
            {row.map(field => {
              const column = regularFields.find(col => col.field === field);
              return (
                <Grid item key={field} sx={{ display: 'flex', flexGrow: 1 }}>
                  {renderField(column, (el) => fieldRefs.current[field] = el)}
                </Grid>
              );
            })}
          </React.Fragment>
        ))}
        {multilineFields.map(column => (
          <Grid item xs={12} key={column.field}>
            {renderField(column)}
          </Grid>
        ))}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {booleanFields.map(column => (
              <Grid item key={column.field}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name={column.field}
                      checked={formData[column.field] || false}
                      onChange={handleChange}
                      disabled={column.field === primaryField}
                    />
                  }
                  label={column.headerName}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Button type="submit" variant="contained" color="primary" sx={{ mr: 2 }}>
            Update
          </Button>
          <Button type="button" variant="outlined" color="warning" onClick={() => onChangeCode(row)}>
            Change code
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DetailPanelForm;
